import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, ChevronRight, Copy, RefreshCw, SlidersHorizontal, X } from "lucide-react";
import moment from "moment";
import ReactJson from "react-json-view";
import PremiumFeatureLocked from "../../components/common/PremiumFeatureLocked";
import GovernanceService from "../../services/api/governance";
import { isPocTenant } from "../../utils/premiumAccess";
import "../../assets/css/audit-history.css";

const RAW_JSON_THEME = {
  base00: "transparent",
  base01: "transparent",
  base02: "transparent",
  base03: "#94a3b8",
  base04: "#64748b",
  base05: "#0f172a",
  base06: "#0f172a",
  base07: "#0f172a",
  base08: "#e11d48",
  base09: "#db2777",
  base0A: "#3F51B5",
  base0B: "#16a34a",
  base0C: "#0891b2",
  base0D: "#2563eb",
  base0E: "#7c3aed",
  base0F: "#0ea5e9",
};

const EMPTY_FILTERS = {
  jobType: "",
  jobId: "",
  decision: "",
  action: "",
  principalId: "",
  policySid: "",
};

const JOB_TYPES = ["WORKFLOW", "LLM_GATEWAY"];
const DECISIONS = ["Allow", "Deny", "Approve"];

function decisionTone(decision) {
  const value = String(decision || "").toLowerCase();
  if (value === "allow" || value === "allowed" || value === "permit") return "allow";
  if (value === "deny" || value === "denied" || value === "block" || value === "blocked") return "deny";
  if (value === "approve" || value === "approved" || value === "approval") return "approve";
  return "neutral";
}

function formatWhen(iso) {
  if (!iso) return { relative: "—", absolute: "—" };
  const parsed = moment.utc(iso).local();
  if (!parsed.isValid()) return { relative: String(iso), absolute: String(iso) };
  return {
    relative: parsed.fromNow(),
    absolute: parsed.format("MMM D, YYYY · h:mm A"),
  };
}

function humanize(value) {
  return String(value || "")
    .replace(/[_:-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function toPretty(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function shortId(value) {
  const text = String(value || "");
  if (text.length <= 14) return text || "—";
  return `${text.slice(0, 8)}…${text.slice(-4)}`;
}

function readAudits(response) {
  const list = Array.isArray(response?.governanceAudits)
    ? response.governanceAudits
    : Array.isArray(response?.data?.governanceAudits)
      ? response.data.governanceAudits
      : [];
  const failed = response?.status === "FAILED" || response?.status === "FAILURE";
  if (failed || (response?.errorMessage && !list.length)) {
    return {
      audits: list,
      error: response?.errorMessage || "Unable to load audit history.",
    };
  }
  return { audits: list, error: null };
}

function activeFilters(filters) {
  return Object.entries(filters).filter(([, value]) => String(value || "").trim());
}

function matchesText(value, needle) {
  const text = String(needle || "").trim().toLowerCase();
  if (!text) return true;
  return String(value || "").toLowerCase().includes(text);
}

function matchesDecision(value, needle) {
  const text = String(needle || "").trim();
  if (!text) return true;
  return decisionTone(value) === decisionTone(text);
}

function matchesFilters(audit, filters) {
  if (!matchesText(audit?.jobType, filters.jobType)) return false;
  if (!matchesText(audit?.jobId, filters.jobId)) return false;
  if (!matchesDecision(audit?.decision, filters.decision)) return false;
  if (!matchesText(audit?.action, filters.action)) return false;
  if (!matchesText(audit?.principalId, filters.principalId)) return false;
  if (!matchesText(audit?.policySid, filters.policySid)) return false;
  return true;
}

function filtersFromSearchParams(searchParams) {
  const next = { ...EMPTY_FILTERS };
  Object.keys(EMPTY_FILTERS).forEach((key) => {
    const value = searchParams.get(key);
    if (value) next[key] = value;
  });
  return next;
}

function writeFiltersToSearchParams(filters, selectedId) {
  const next = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    const text = String(value || "").trim();
    if (text) next.set(key, text);
  });
  if (selectedId) next.set("audit", selectedId);
  return next;
}

function filtersEqual(left, right) {
  return Object.keys(EMPTY_FILTERS).every(
    (key) => String(left?.[key] || "").trim() === String(right?.[key] || "").trim()
  );
}

function searchParamsEqual(left, right) {
  const a = new URLSearchParams(left).toString();
  const b = new URLSearchParams(right).toString();
  return a === b;
}

function isEmailRecord(value) {
  return Boolean(
    value
    && typeof value === "object"
    && !Array.isArray(value)
    && (value.from || value.subject || value.body)
  );
}

function collectTools(node, acc = []) {
  if (Array.isArray(node)) {
    node.forEach((item) => collectTools(item, acc));
    return acc;
  }
  if (!node || typeof node !== "object") return acc;
  const entries = Object.entries(node);
  const toolEntries = entries.filter(([, value]) => (
    value
    && typeof value === "object"
    && !Array.isArray(value)
    && ("result" in value || "success" in value)
  ));
  if (toolEntries.length && toolEntries.length === entries.length) {
    toolEntries.forEach(([name, payload]) => acc.push({ name, payload }));
    return acc;
  }
  entries.forEach(([, value]) => collectTools(value, acc));
  return acc;
}

function PromptText({ text }) {
  const parts = String(text || "").split(/(<[^>]+>)/g);
  return parts.map((part, index) => {
    if (/^<[^>]+>$/.test(part)) {
      const label = part.slice(1, -1).replace(/_/g, " ");
      return <span key={`${part}-${index}`} className="ah-token">{label}</span>;
    }
    return <React.Fragment key={`t-${index}`}>{part}</React.Fragment>;
  });
}

function CopyButton({ value, label = "Copy" }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      className={`ah-copy${copied ? " is-copied" : ""}`}
      title={copied ? "Copied" : label}
      aria-label={copied ? "Copied" : label}
      onClick={async (event) => {
        event.stopPropagation();
        try {
          await navigator.clipboard.writeText(String(value));
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1400);
        } catch {
          setCopied(false);
        }
      }}
    >
      {copied ? <Check size={13} strokeWidth={2.4} /> : <Copy size={13} strokeWidth={2.1} />}
    </button>
  );
}

function DecisionPill({ decision }) {
  const tone = decisionTone(decision);
  return <span className={`ah-pill ${tone}`}>{decision || "Unknown"}</span>;
}

function MetaRow({ label, value, mono = false, copyable = false, title, copyValue }) {
  const shown = value == null || value === "" ? "—" : String(value);
  const toCopy = copyValue ?? title ?? value;
  return (
    <div className="ah-meta-row">
      <span className="ah-meta-label">{label}</span>
      <span className={`ah-meta-value${mono ? " ah-mono" : ""}`} title={title || (shown !== "—" ? shown : undefined)}>
        {shown}
      </span>
      {copyable && toCopy ? <CopyButton value={toCopy} /> : null}
    </div>
  );
}

function CheckChip({ label, ok }) {
  return (
    <span className={`ah-check-chip${ok ? " is-ok" : " is-bad"}`}>
      <span className="ah-check-dot" aria-hidden="true" />
      {label}
    </span>
  );
}

function CompactTrace({ trace = [] }) {
  if (!trace.length) {
    return <p className="ah-muted">No policy trace recorded.</p>;
  }
  return (
    <div className="ah-trace">
      {trace.map((step, index) => (
        <article key={`${step?.sid || "step"}-${index}`} className="ah-trace-card">
          <div className="ah-trace-head">
            <div className="ah-trace-sid">{step?.sid || `Step ${index + 1}`}</div>
            <span className="ah-pill">{index + 1}</span>
          </div>
          <div className="ah-check-row">
            <CheckChip label="Principal" ok={Boolean(step?.principal)} />
            <CheckChip label="Action" ok={Boolean(step?.action)} />
            <CheckChip label="Resource" ok={Boolean(step?.resource)} />
            <CheckChip label="Condition" ok={Boolean(step?.condition?.success)} />
            <CheckChip label="Threat" ok={Boolean(step?.threat?.success)} />
          </div>
          {Array.isArray(step?.plugins) && step.plugins.length ? (
            <div className="ah-plugin-list">
              {step.plugins.map((plugin, pluginIndex) => (
                <div key={`${plugin?.provider || "plugin"}-${pluginIndex}`} className="ah-plugin">
                  <div className="ah-plugin-top">
                    <div className="ah-plugin-name">
                      {humanize(plugin?.provider || "Plugin")}
                      {plugin?.mode ? ` · ${plugin.mode}` : ""}
                    </div>
                    <span className={`ah-pill ${plugin?.success ? "allow" : "deny"}`}>
                      {plugin?.success ? "Ok" : "Fail"}
                    </span>
                  </div>
                  <div className="ah-plugin-meta">
                    {plugin?.transformed ? "Transformed" : "Unchanged"}
                    {plugin?.result?.data?.redacted ? " · Redacted" : ""}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </article>
      ))}
    </div>
  );
}

function CompactSource({ source }) {
  const tools = useMemo(() => collectTools(source?.metadata?.previous_agent_result), [source]);
  if (!source) return <p className="ah-muted">No source attached.</p>;

  return (
    <div className="ah-source">
      <div className="ah-meta-list">
        <MetaRow label="Type" value={humanize(source.type)} />
        <MetaRow label="Id" value={source.id} mono copyable title={source.id} />
        <MetaRow label="Agent" value={humanize(source?.metadata?.agent_type)} />
        <MetaRow label="Resource" value={source?.metadata?.resource ? `${source.metadata.resource.type}:${source.metadata.resource.id}` : null} mono />
      </div>
      {tools.map((tool) => {
        const emails = Array.isArray(tool.payload?.result)
          ? tool.payload.result.filter(isEmailRecord)
          : [];
        return (
          <div key={tool.name} className="ah-plugin">
            <div className="ah-plugin-top">
              <div className="ah-plugin-name">{humanize(tool.name)}</div>
              <span className={`ah-pill ${tool.payload?.success ? "allow" : "deny"}`}>
                {tool.payload?.success ? "Ok" : "Fail"}
              </span>
            </div>
            {emails.map((email, index) => (
              <div key={`${email.subject || "email"}-${index}`} className="ah-email">
                <div className="ah-email-from">{email.from || "Unknown sender"}</div>
                <div className="ah-email-subject">{email.subject || "No subject"}</div>
                {email.body ? <p className="ah-email-body">{email.body}</p> : null}
              </div>
            ))}
            {!emails.length && tool.payload?.result != null ? (
              <pre className="ah-code">{toPretty(tool.payload.result)}</pre>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function AuditDetailPanel({ audit, loading, missing, onClose }) {
  if (loading && !audit) {
    return (
      <aside className="ah-drawer is-open" aria-label="Audit detail">
        <div className="ah-drawer-head">
          <div>
            <div className="ah-drawer-kicker">Event detail</div>
            <div className="ah-drawer-title">Loading…</div>
          </div>
          <button type="button" className="ah-icon-btn" onClick={onClose} aria-label="Close detail">
            <X size={16} />
          </button>
        </div>
        <div className="ah-drawer-body">
          <div className="ah-skel" style={{ height: 72 }} />
          <div className="ah-skel" style={{ height: 120 }} />
          <div className="ah-skel" style={{ height: 180 }} />
        </div>
      </aside>
    );
  }

  if (missing) {
    return (
      <aside className="ah-drawer is-open" aria-label="Audit detail">
        <div className="ah-drawer-head">
          <div>
            <div className="ah-drawer-kicker">Event detail</div>
            <div className="ah-drawer-title">Not found</div>
          </div>
          <button type="button" className="ah-icon-btn" onClick={onClose} aria-label="Close detail">
            <X size={16} />
          </button>
        </div>
        <div className="ah-drawer-body">
          <p className="ah-muted">This event is not in the current results. Close and pick another from the list.</p>
        </div>
      </aside>
    );
  }

  if (!audit) return null;

  const when = formatWhen(audit.timestamp);
  const approval = audit.approval || {};
  const matched = audit?.diagnostics?.matched_policies || [];
  const prompt = audit?.diagnostics?.context?.prompt;
  const tone = decisionTone(audit.decision);

  return (
    <aside className={`ah-drawer is-open is-${tone}`} aria-label="Audit detail">
      <div className="ah-drawer-head">
        <div className="ah-drawer-head-copy">
          <div className="ah-drawer-kicker">Event detail</div>
          <div className="ah-drawer-title-row">
            <DecisionPill decision={audit.decision} />
            {audit.jobType ? <span className="ah-pill">{audit.jobType}</span> : null}
          </div>
          <h2 className="ah-drawer-title">{audit.action || "Governance decision"}</h2>
          <p className="ah-drawer-resource" title={audit.resource || undefined}>{audit.resource || "—"}</p>
          <p className="ah-drawer-time">{when.absolute} · {when.relative}</p>
        </div>
        <button type="button" className="ah-icon-btn" onClick={onClose} aria-label="Close detail">
          <X size={16} />
        </button>
      </div>

      <div className="ah-drawer-body">
        {audit.reason ? <div className="ah-reason">{audit.reason}</div> : null}

        <section className="ah-block">
          <h3>Overview</h3>
          <div className="ah-meta-list">
            <MetaRow label="Job type" value={audit.jobType} />
            <MetaRow label="Job id" value={shortId(audit.jobId)} mono copyable title={audit.jobId} />
            <MetaRow label="Policy" value={audit.policySid} />
            <MetaRow label="Principal" value={audit.principalId} mono copyable title={audit.principalId} />
            <MetaRow label="Request" value={shortId(audit.requestId)} mono copyable title={audit.requestId} />
            <MetaRow
              label="Approval"
              value={approval.required ? (approval.role || approval.id || "Required") : "Not required"}
            />
            <MetaRow label="Audit id" value={shortId(audit.id)} mono copyable title={audit.id} />
          </div>
        </section>

        <section className="ah-block">
          <div className="ah-block-head">
            <h3>Context</h3>
            {prompt ? <CopyButton value={prompt} label="Copy context" /> : null}
          </div>
          {matched.length ? (
            <div className="ah-policy-row">
              {matched.map((sid) => (
                <span key={sid} className="ah-pill allow">{sid}</span>
              ))}
            </div>
          ) : null}
          {prompt ? (
            <div className="ah-prompt"><PromptText text={prompt} /></div>
          ) : (
            <p className="ah-muted">No evaluated prompt.</p>
          )}
        </section>

        <section className="ah-block">
          <h3>Policy trace</h3>
          <CompactTrace trace={audit?.diagnostics?.trace || []} />
        </section>

        <section className="ah-block">
          <h3>Source</h3>
          <CompactSource source={audit.source} />
        </section>

        <section className="ah-block">
          <div className="ah-block-head">
            <h3>Raw result</h3>
            <CopyButton value={toPretty(audit)} label="Copy JSON" />
          </div>
          <div className="ah-json">
            <ReactJson
              src={audit}
              name={false}
              collapsed={2}
              enableClipboard={false}
              displayDataTypes={false}
              displayObjectSize
              quotesOnKeys={false}
              sortKeys={false}
              theme={RAW_JSON_THEME}
              style={{ background: "transparent", fontSize: 12 }}
            />
          </div>
        </section>
      </div>
    </aside>
  );
}

function AuditHistoryContent() {
  const pageRef = useRef(null);
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("audit") || "";
  const initialFilters = useMemo(() => filtersFromSearchParams(params), []);
  const [filters, setFilters] = useState(initialFilters);
  const [query, setQuery] = useState(initialFilters);
  const [showMore, setShowMore] = useState(() => (
    Boolean(initialFilters.action || initialFilters.principalId || initialFilters.policySid)
  ));
  const [audits, setAudits] = useState([]);
  const [byId, setById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery((prev) => (filtersEqual(prev, filters) ? prev : { ...filters }));
      setParams((current) => {
        const audit = current.get("audit") || "";
        const next = writeFiltersToSearchParams(filters, audit);
        return searchParamsEqual(current, next) ? current : next;
      }, { replace: true });
    }, 320);
    return () => window.clearTimeout(timer);
  }, [filters, setParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    GovernanceService.listAudits(query)
      .then((response) => {
        if (cancelled) return;
        const parsed = readAudits(response);
        setAudits(parsed.audits);
        setError(parsed.error || "");
        setById((prev) => {
          const next = { ...prev };
          parsed.audits.forEach((item) => {
            if (item?.id) next[item.id] = item;
          });
          return next;
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || "Unable to load audit history.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query, reloadKey]);

  useEffect(() => {
    if (!selectedId) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") {
        setParams(writeFiltersToSearchParams(filters, ""));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filters, selectedId, setParams]);

  const setFilter = (key, value) => {
    setFilters((prev) => {
      if (String(prev[key] || "") === String(value || "")) return prev;
      return { ...prev, [key]: value };
    });
  };

  const selectDecisionStat = (decision) => {
    setFilters((prev) => {
      const nextDecision = prev.decision === decision ? "" : decision;
      if (nextDecision === prev.decision) return prev;
      return { ...prev, decision: nextDecision };
    });
  };

  const openAudit = (audit) => {
    if (!audit?.id) return;
    if (selectedId === audit.id) {
      setParams(writeFiltersToSearchParams(filters, ""));
      return;
    }
    setById((prev) => ({ ...prev, [audit.id]: audit }));
    setParams(writeFiltersToSearchParams(filters, audit.id));
  };

  const closeAudit = () => {
    setParams(writeFiltersToSearchParams(filters, ""));
  };

  const applied = activeFilters(filters);
  const visible = audits
    .filter((audit) => matchesFilters(audit, filters))
    .sort((left, right) => {
      const leftTime = new Date(left?.timestamp || 0).getTime();
      const rightTime = new Date(right?.timestamp || 0).getTime();
      return rightTime - leftTime;
    });
  const statsBase = audits.filter((audit) => matchesFilters(audit, { ...filters, decision: "" }));
  const allowed = statsBase.filter((item) => decisionTone(item.decision) === "allow").length;
  const denied = statsBase.filter((item) => decisionTone(item.decision) === "deny").length;
  const approved = statsBase.filter((item) => decisionTone(item.decision) === "approve").length;
  const selected = selectedId ? byId[selectedId] : null;
  const panelOpen = Boolean(selectedId);
  const moreActive = ["action", "principalId", "policySid"].some((key) => filters[key]);
  const showListSkeleton = loading && !visible.length;
  const showListBusy = loading && visible.length > 0;

  return (
    <div className={`ah-page${panelOpen ? " has-drawer" : ""}`} ref={pageRef}>
      <div className="ah-shell">
        <div className="ah-main">
          <header className="ah-header">
            <div>
              <div className="ah-kicker"><span className="ah-kicker-mark">✦</span> Governance</div>
              <h1 className="ah-title">Audit history</h1>
              <p className="ah-subtitle">
                Every governance decision for this workspace, with the policy, plugin, and source that produced it.
              </p>
            </div>
            <div className="ah-stats" role="group" aria-label="Decision filters">
              <button
                type="button"
                className={`ah-stat${!filters.decision ? " is-on" : ""}`}
                onClick={() => selectDecisionStat("")}
                aria-pressed={!filters.decision}
                disabled={loading}
              >
                <span className="ah-stat-value">{loading && !statsBase.length ? "—" : statsBase.length}</span>
                <span className="ah-stat-label">Events</span>
              </button>
              <button
                type="button"
                className={`ah-stat is-allow${filters.decision === "Allow" ? " is-on" : ""}`}
                onClick={() => selectDecisionStat("Allow")}
                aria-pressed={filters.decision === "Allow"}
                disabled={loading}
              >
                <span className="ah-stat-value">{loading && !statsBase.length ? "—" : allowed}</span>
                <span className="ah-stat-label">Allowed</span>
              </button>
              <button
                type="button"
                className={`ah-stat is-deny${filters.decision === "Deny" ? " is-on" : ""}`}
                onClick={() => selectDecisionStat("Deny")}
                aria-pressed={filters.decision === "Deny"}
                disabled={loading}
              >
                <span className="ah-stat-value">{loading && !statsBase.length ? "—" : denied}</span>
                <span className="ah-stat-label">Denied</span>
              </button>
              <button
                type="button"
                className={`ah-stat is-approve${filters.decision === "Approve" ? " is-on" : ""}`}
                onClick={() => selectDecisionStat("Approve")}
                aria-pressed={filters.decision === "Approve"}
                disabled={loading}
              >
                <span className="ah-stat-value">{loading && !statsBase.length ? "—" : approved}</span>
                <span className="ah-stat-label">Approved</span>
              </button>
            </div>
          </header>

          <section className="ah-panel ah-filters">
            <div className="ah-filter-grid">
              <label className="ah-field">
                <span>Job type</span>
                <input
                  list="ah-job-types"
                  value={filters.jobType}
                  placeholder="All types"
                  onChange={(event) => setFilter("jobType", event.target.value)}
                />
                <datalist id="ah-job-types">
                  {JOB_TYPES.map((type) => <option key={type} value={type} />)}
                </datalist>
              </label>
              <label className="ah-field">
                <span>Job id</span>
                <input
                  value={filters.jobId}
                  placeholder="Filter by job"
                  onChange={(event) => setFilter("jobId", event.target.value)}
                />
              </label>
              <label className="ah-field">
                <span>Decision</span>
                <select value={filters.decision} onChange={(event) => setFilter("decision", event.target.value)}>
                  <option value="">All decisions</option>
                  {DECISIONS.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </label>
            </div>

            {showMore || moreActive ? (
              <div className="ah-more-grid">
                <label className="ah-field">
                  <span>Action</span>
                  <input
                    value={filters.action}
                    placeholder="email:send"
                    onChange={(event) => setFilter("action", event.target.value)}
                  />
                </label>
                <label className="ah-field">
                  <span>Principal</span>
                  <input
                    value={filters.principalId}
                    placeholder="Principal id"
                    onChange={(event) => setFilter("principalId", event.target.value)}
                  />
                </label>
                <label className="ah-field">
                  <span>Policy</span>
                  <input
                    value={filters.policySid}
                    placeholder="Policy sid"
                    onChange={(event) => setFilter("policySid", event.target.value)}
                  />
                </label>
              </div>
            ) : null}

            <div className="ah-filter-bar">
              <div className="ah-chips">
                <button
                  type="button"
                  className={`ah-chip${!filters.jobType ? " is-on" : ""}`}
                  onClick={() => setFilter("jobType", "")}
                >
                  All types
                </button>
                {JOB_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={`ah-chip${filters.jobType === type ? " is-on" : ""}`}
                    onClick={() => setFilter("jobType", filters.jobType === type ? "" : type)}
                  >
                    {type}
                  </button>
                ))}
              </div>
              <div className="ah-filter-actions">
                <button
                  type="button"
                  className={`ah-btn${showMore || moreActive ? " is-on" : ""}`}
                  onClick={() => setShowMore((open) => !open)}
                >
                  <SlidersHorizontal size={14} />
                  More filters
                </button>
                {applied.length ? (
                  <button type="button" className="ah-btn" onClick={() => setFilters({ ...EMPTY_FILTERS })}>
                    Clear
                  </button>
                ) : null}
                <button
                  type="button"
                  className="ah-btn primary"
                  onClick={() => setReloadKey((value) => value + 1)}
                  disabled={loading}
                >
                  <RefreshCw size={14} className={loading ? "ah-spin" : ""} />
                  Refresh
                </button>
              </div>
            </div>
          </section>

          <div className="ah-list-head">
            <span>
              {loading
                ? "Loading history…"
                : `${visible.length} ${visible.length === 1 ? "event" : "events"}`}
            </span>
            {applied.length ? <span>{applied.length} filters applied</span> : <span>Newest first</span>}
          </div>

          {error ? (
            <div className="ah-error">
              <h2>Couldn’t load audit history</h2>
              <p>{error}</p>
              <button type="button" className="ah-btn primary" onClick={() => setReloadKey((value) => value + 1)}>
                Try again
              </button>
            </div>
          ) : null}

          {showListSkeleton ? (
            <div className="ah-list" aria-busy="true">
              {Array.from({ length: 5 }).map((_, index) => <div key={index} className="ah-skel" />)}
            </div>
          ) : null}

          {!loading && !error && !visible.length ? (
            <div className="ah-panel ah-empty">
              <h2>{applied.length ? "No matching audits" : "No audit events yet"}</h2>
              <p>
                {applied.length
                  ? "Try another job type, job id, or clear the filters to see the full history."
                  : "Decisions will show up here after a workflow or agent is evaluated by governance."}
              </p>
              {applied.length ? (
                <button type="button" className="ah-btn" onClick={() => setFilters({ ...EMPTY_FILTERS })}>
                  Clear filters
                </button>
              ) : null}
            </div>
          ) : null}

          {visible.length ? (
            <div className={`ah-list${showListBusy ? " is-loading" : ""}`} aria-busy={showListBusy}>
              {showListBusy ? (
                <div className="ah-list-loading" role="status">
                  <RefreshCw size={16} className="ah-spin" />
                  Loading…
                </div>
              ) : null}
              {visible.map((audit) => {
                const when = formatWhen(audit.timestamp);
                const tone = decisionTone(audit.decision);
                const active = selectedId === audit.id;
                return (
                  <button
                    key={audit.id || `${audit.jobId}-${audit.timestamp}`}
                    type="button"
                    className={`ah-card is-${tone}${active ? " is-active" : ""}`}
                    onClick={() => openAudit(audit)}
                    aria-pressed={active}
                    disabled={loading}
                  >
                    <div className="ah-card-top">
                      <div className="ah-card-title">
                        <DecisionPill decision={audit.decision} />
                        <span className="ah-action">{audit.action || "Untitled action"}</span>
                      </div>
                      <span className="ah-when" title={when.absolute}>{when.relative}</span>
                    </div>
                    <div className="ah-resource">{audit.resource || "—"}</div>
                    <div className="ah-card-foot">
                      <span>{audit.jobType || "Job"}</span>
                      {audit.policySid ? (
                        <>
                          <span className="ah-dot">·</span>
                          <span>{audit.policySid}</span>
                        </>
                      ) : null}
                      <ChevronRight size={16} className="ah-card-chev" />
                    </div>
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        {panelOpen ? (
          <>
            <button type="button" className="ah-drawer-scrim" aria-label="Close detail" onClick={closeAudit} />
            <AuditDetailPanel
              audit={selected}
              loading={loading && !selected}
              missing={!loading && !selected}
              onClose={closeAudit}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}

export default function AuditHistoryPage() {
  if (isPocTenant()) {
    return <PremiumFeatureLocked featureName="Audit history" style={{ flex: 1, minHeight: 0 }} />;
  }
  return <AuditHistoryContent />;
}
